import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { useUser } from "../../helper/userContext";
import { useSession } from "components/helper/sessionContext";
import {
    backgroundFrame,
    FRAME_BORDER_RADIUS,
    frameFillBackground,
    onBackgroundImage,
} from "components/common/backgroundFrame";
import packageJson from "../../../../package.json";
import { CSS_VARIABLES } from "enums/cssVariables";
import { EVENT } from "types/socketEventsType";

import LanguageSelector from "../LanguageSelector";
import SessionInformation from "./SessionInformation";
import PanToolIcon from "@mui/icons-material/PanTool";
import DoNotTouchIcon from "@mui/icons-material/DoNotTouch";
import { ERROR } from "types/snackBar.constant";

export enum FloorRequestAction {
    REQUEST = "request",
    CANCEL = "cancel",
}
type RequestFloorBtnProps = {
    isActive: boolean;
};

interface FloorRequestError {
    status?: string;
    code?: string;
}

const FLOOR_REQUEST_ERROR_CODE = {
    NOT_AUTHORIZED: "QBO_FLOOR_REQUEST_NOT_AUTHORIZED",
    CANNOT_CANCEL_WHILE_SPEAKING:
        "QBO_FLOOR_REQUEST_CANNOT_CANCEL_WHILE_SPEAKING",
} as const;

const getFloorRequestErrorMessage = (errorCode?: string): string => {
    switch (errorCode) {
        case FLOOR_REQUEST_ERROR_CODE.NOT_AUTHORIZED:
            return "errorFloorRequestNotAllowed";

        case FLOOR_REQUEST_ERROR_CODE.CANNOT_CANCEL_WHILE_SPEAKING:
            return "errorFloorRequestCannotCancelWhileSpeaking";

        default:
            return "errorFloorRequestFailed";
    }
};

const Footer = () => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const userContext = useUser();
    const {
        session,
        socket,
        isSessionActive,
        isRequestingFloor,
        showRequestFloorButton,
        setIsRequestingFloor,
    } = useSession();

    const codeOnline = session?.codeOnline;

    return (
        <WrapperFooter dir="ltr">
            {/* Request Floor button  */}
            {socket && userContext.user && showRequestFloorButton ? (
                <RequestFloorBtn
                    isActive={isRequestingFloor}
                    onClick={async () => {
                        const action = isRequestingFloor
                            ? FloorRequestAction.CANCEL
                            : FloorRequestAction.REQUEST;

                        try {
                            await new Promise<{ [key: string]: any }>(
                                (resolve, reject) => {
                                    socket.emit(
                                        EVENT.TOGGLE_PARTICIPANT_FLOOR_REQUEST,
                                        action,
                                        (res: any) => {
                                            if ("SUCCESS" === res.status) {
                                                setIsRequestingFloor(
                                                    action ===
                                                        FloorRequestAction.REQUEST,
                                                );
                                                resolve(res);
                                            } else {
                                                reject(res);
                                            }
                                        },
                                    );
                                },
                            );
                        } catch (err) {
                            const floorRequestError = err as FloorRequestError;
                            enqueueSnackbar(
                                t(
                                    getFloorRequestErrorMessage(
                                        floorRequestError.code,
                                    ),
                                ),
                                {
                                    variant: ERROR,
                                },
                            );
                        }
                    }}
                >
                    {isRequestingFloor ? (
                        <>
                            <DoNotTouchIcon fontSize="medium" />
                            {t("requestFloor.cancel")}
                        </>
                    ) : (
                        <>
                            <PanToolIcon fontSize="medium" />
                            {t("requestFloor.request")}
                        </>
                    )}
                </RequestFloorBtn>
            ) : null}
            <Wrapper>
                {/* Session information button */}
                {socket && userContext.user && codeOnline ? (
                    <SessionInformation
                        codeOnline={codeOnline}
                        isSessionActive={isSessionActive}
                    ></SessionInformation>
                ) : null}
                <WrapperInformation>
                    <a
                        href={window.env.REACT_APP_HOME}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {window.env.REACT_APP_THEME}
                    </a>
                    <span>{packageJson.version}</span>
                    <span>-</span>
                    <a
                        href={t("privacyLink")}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {t("privacyPolicyText")}
                    </a>
                    <span>-</span>
                    <LanguageSelector />
                </WrapperInformation>
            </Wrapper>
        </WrapperFooter>
    );
};

const WrapperFooter = styled.footer`
    display: flex;
    flex-direction: column;

    margin-top: auto;
    padding-bottom: 1rem;
    /* Over a background image the footer frame sticks to the bottom of the screen. */
    ${onBackgroundImage(css`
        padding-bottom: 0;
    `)}
`;

const WrapperInformation = styled.div`
    display: flex;
    align-items: center;
    padding-left: 0.3rem;
    & * {
        padding: 0.13rem;
        color: #333;
    }
    /* Inside the frame the padding leaves less room: let the links wrap instead of overflowing. */
    ${onBackgroundImage(css`
        flex-wrap: wrap;
        justify-content: center;
        padding-left: 0;
        /* Explicit sizing: the "& *" padding above gives the select an unreliable height, clipped in some browsers. */
        select {
            box-sizing: border-box;
            height: auto;
            padding: 0.125rem 0.375rem;
            line-height: 1.4;
            font-size: inherit;
            border: 1px solid rgb(0 0 0 / 25%);
            border-radius: 4px;
            /* Shares the footer frame background, like the logout button. */
            background-color: transparent;
        }
        /* Some browsers draw the dropdown list with the select background: keep it readable. */
        option {
            background-color: #ffffff;
        }
    `)}
`;
const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 0.7rem;
    margin-top: auto;
    justify-content: center;
    @media (max-width: 768px) {
        flex-direction: column;
    }
    /* One frame for the session code, the logout button and the links. */
    ${backgroundFrame(css`
        padding: 0.5rem 1rem;
        border-bottom: none;
        border-radius: ${FRAME_BORDER_RADIUS} ${FRAME_BORDER_RADIUS} 0 0;
    `)}
`;
const RequestFloorBtn = styled.button<RequestFloorBtnProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.6rem;
    margin-bottom: 2.5em;
    min-height: 2.75rem;
    padding: 0.5rem;
    border-radius: 3px;
    font-size: 1rem;
    background: ${(p) =>
        p.isActive
            ? `var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR})`
            : "#fff"};

    color: ${(p) =>
        p.isActive
            ? `var(${CSS_VARIABLES.PRIMARY_BUTTON_TEXT_COLOR})`
            : `var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR})`};

    border: 1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
    cursor: pointer;
    :focus-visible {
        outline: solid 2px #010203;
        outline-offset: 2px;
    }
    /* Over a background image the idle button matches the frames instead of an opaque white. */
    ${(p) => !p.isActive && frameFillBackground}
`;
export default Footer;
